import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api, eur } from "@/lib/api";
import { ProductImage } from "@/components/shop/ProductImage";

const Item = ({ p, active, onPick, onHover, idx }) => (
  <li>
    <button type="button" onMouseDown={(e) => { e.preventDefault(); onPick(p); }} onMouseEnter={() => onHover(idx)}
      className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${active ? "bg-amber-500/15" : "hover:bg-black/5"}`}
      data-testid={`search-suggestion-${p.id}`}>
      <ProductImage src={p.image} w={300} width={44} height={44} className="h-11 w-11 shrink-0 rounded-md bg-stone-100 object-contain" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{p.name}</span>
        <span className="block truncate text-xs text-stone-500">
          {p.card_number && <span className="mr-1.5 rounded bg-black/5 px-1 font-mono text-[11px] text-stone-700" data-no-translate data-testid={`search-suggestion-number-${p.id}`}>#{p.card_number}</span>}
          {p.preorder ? "Preordine" : p.stock > 0 ? p.set_name || "Disponibile" : "Esaurito"}
        </span>
      </span>
      <span className="shrink-0 font-mono text-sm font-bold text-amber-600">{eur(p.price)}</span>
    </button>
  </li>
);

export const SearchSuggest = ({ visible }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const seq = useRef(0);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setItems([]); return; }
    const id = ++seq.current;
    const t = setTimeout(() => {
      api.get("/products/suggest", { params: { q: term } }).then((r) => {
        if (id === seq.current) { setItems(r.data); setActive(-1); setOpen(true); }
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const showAll = () => { setOpen(false); navigate(`/catalogo${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); };
  const pick = (p) => { setOpen(false); setQ(""); navigate(`/prodotto/${p.id}`); };
  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? items.length - 1 : a - 1)); }
    else if (e.key === "Escape") setOpen(false);
  };
  const submit = (e) => { e.preventDefault(); if (open && active >= 0) pick(items[active]); else showAll(); };
  const show = open && q.trim().length >= 2;

  return (
    <form onSubmit={submit} className={`relative order-last ${visible ? "flex" : "hidden"} basis-full items-center sm:absolute sm:left-1/2 sm:top-1/2 sm:w-[min(36rem,45vw)] sm:-translate-x-1/2 sm:-translate-y-1/2 rounded-full border border-black/15 bg-[#FFFFFF] px-3 focus-within:border-amber-500 sm:flex`} data-testid="nav-search-form">
      <Search className="h-4 w-4 text-stone-500" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} onFocus={() => items.length && setOpen(true)} onBlur={() => setOpen(false)}
        placeholder="Cerca..." role="combobox" aria-expanded={show} autoComplete="off"
        data-testid="nav-search-input" className="h-9 w-full bg-transparent px-2 text-sm outline-none placeholder:text-stone-500" />
      {show && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-xl animate-in fade-in slide-in-from-top-1" data-testid="search-suggestions">
          {items.length ? (
            <ul className="max-h-[60vh] overflow-y-auto py-1">
              {items.map((p, i) => <Item key={p.id} p={p} idx={i} active={i === active} onPick={pick} onHover={setActive} />)}
            </ul>
          ) : <p className="px-4 py-3 text-sm text-stone-500" data-testid="search-no-results">Nessun prodotto trovato</p>}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); showAll(); }} className="block w-full border-t border-black/10 px-4 py-2.5 text-left text-sm font-semibold text-cyan-700 hover:bg-black/5" data-testid="search-see-all">
            Vedi tutti i risultati per “<span data-no-translate>{q.trim()}</span>”
          </button>
        </div>
      )}
    </form>
  );
};
